import React, { Fragment, useEffect, useState, useRef } from "react";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import styled, { keyframes, css } from "styled-components";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { formatToIST, getErrorMessage } from "../../../helpers/functions";
import CaseService from "../../../services/CaseService";
import { AssigninBulkCaseId } from "../../../helpers/validationSchema";
import { Formik } from "formik";
import FormField from "../../common/FormField/FormField";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { Modal, CircularProgress } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import AlertDialog from "../../common/Alert";
import CaesLifeCycleStatus from "./CaseLifeCycleStatus";
import useUser from "../../../hooks/useUser";
import moment from "moment";
import * as XLSX from "xlsx";
import ResponseModal from "../AssignNeutralBulkCase/ResponseModal";

const inputModes = { MANUAL: "manual", EXCEL: "excel" };

const inputs = [
  {
    type: "number",
    name: "startCaseId",
    label: "From Case ID",
    required: true,
    autoFocus: true,
    helperText: "Enter the first Case ID in the range",
  },
  {
    type: "number",
    name: "endCaseId",
    label: "To Case ID",
    required: true,
    helperText: "Enter the last Case ID in the range",
  },
  {
    type: "text",
    name: "exceptCaseIds",
    label: "Except Case IDs (comma separated)",
    required: false,
    placeholder: "e.g., 102, 109, 120",
    helperText: "Optional: exclude specific cases from the selected range",
  },
  {
    type: "select",
    name: "resolutionKind",
    label: "Case Type",
    required: true,
    menuItems: [
      { label: "Arbitration", value: "arbitration" },
      { label: "Mediation", value: "mediation" },
      { label: "Negotiation", value: "negotiation" },
    ],
  },
];

const Index = ({ lifecycleModal, setLifeCycleModal }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const { userObject } = useUser();

  const [lifeCycle, setLifeCycle] = useState(false);
  const [status, setStatus] = useState([]);
  const [existingStatuses, setExistingStatuses] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [mode, setMode] = useState(inputModes.MANUAL);
  const [excelCaseIds, setExcelCaseIds] = useState([]);
  const [showResponseModal, setShowResponseModal] = useState({ state: false });

  const formValuesRef = useRef({});

  const fetchCurrentStatuses = async (startId, endId) => {
    if (!startId || !endId || startId > endId) {
      setExistingStatuses([]);
      return;
    }
    try {
      setLoadingStatus(true);
      const response = await CaseService.getCaseStatusRange?.({
        startCaseId: parseInt(startId),
        endCaseId: parseInt(endId),
      });
      const data = response?.data || [];
      setExistingStatuses(
        data.map((item) => ({
          label: item.statusLabel || "Unknown",
          value: item.statusValue || item.status,
          date: item.statusDate ? new Date(item.statusDate) : new Date(),
        })),
      );
    } catch (err) {
      console.warn("Failed to load current statuses:", err);
      setExistingStatuses([]);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    const values = formValuesRef.current;
    if (
      values.startCaseId &&
      values.endCaseId &&
      values.startCaseId <= values.endCaseId
    ) {
      fetchCurrentStatuses(values.startCaseId, values.endCaseId);
    } else {
      setExistingStatuses([]);
    }
  }, [formValuesRef.current.startCaseId, formValuesRef.current.endCaseId]);

  const handleFileUpload = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;
    setFieldValue("fileName", file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (data.length === 0) {
          enqueueSnackbar("Excel file is empty", { variant: "error" });
          return;
        }
        const headers = (data[0] || []).map((h) =>
          (h || "").toString().toLowerCase().trim(),
        );
        const colIdx = headers.findIndex((h) =>
          [
            "caseid",
            "case id",
            "id",
            "case_id",
            "case-id",
            "case id/dispute no",
          ].includes(h),
        );
        if (colIdx === -1) {
          enqueueSnackbar(
            "Column not found. Please use one of: caseId, Case Id, id",
            { variant: "error" },
          );
          return;
        }
        const ids = data
          .slice(1)
          .map((row) => row[colIdx])
          .filter((id) => id !== undefined && id !== null && id !== "")
          .map(Number)
          .filter((id) => !isNaN(id) && id > 0);
        if (ids.length === 0) {
          enqueueSnackbar("No valid case IDs found in the file", {
            variant: "warning",
          });
          return;
        }
        setExcelCaseIds(ids);
        enqueueSnackbar(
          `${ids.length} case ID${ids.length > 1 ? "s" : ""} loaded successfully`,
          { variant: "success" },
        );
      } catch (err) {
        enqueueSnackbar(
          "Failed to read Excel file. Please try a valid .xlsx file.",
          { variant: "error" },
        );
      }
    };
    reader.onerror = () =>
      enqueueSnackbar("Error reading file", { variant: "error" });
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  const onFormSubmit = async (values) => {
    try {
      setLoader({ state: true, message: "Updating cases..." });
      const sorted = status.sort((a, b) => new Date(a.date) - new Date(b.date));
      let payload = {};
      if (mode === inputModes.MANUAL) {
        payload = {
          startCaseId: parseInt(values.startCaseId),
          endCaseId: parseInt(values.endCaseId),
          exceptCaseIds: values.exceptCaseIds || "",
          resolutionKind: values.resolutionKind,
          status: sorted.map((item) => item.value),
          currentUser: userObject?.name || "System",
          remarks: values.remarks || "",
          stagesDate: sorted.map((item) => formatToIST(item.date)),
          typeOfUpdate: "caseUpdate",
        };
      } else {
        if (excelCaseIds.length === 0) {
          enqueueSnackbar("Please upload a valid Excel file with case IDs", {
            variant: "warning",
          });
          setLoader({ state: false });
          return;
        }
        payload = {
          caseIds: excelCaseIds.map((id) => Number(id)),
          resolutionKind: values.resolutionKind,
          status: sorted.map((item) => item.value),
          currentUser: userObject?.name || "System",
          remarks: values.remarks || "",
          stagesDate: sorted.map((item) => formatToIST(item.date)),
          typeOfUpdate: "caseUpdate",
        };
      }
      const response = await CaseService.NeutralBulkCase(payload);
      if (response) {
        setShowResponseModal({ state: true, metaData: response });
        setLifeCycleModal({ state: false });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
      setStatus([]);
    }
  };

  const SelectedStatusSummary = () => {
    if (!status?.length) return null;
    return (
      <SelectedStatusPanel>
        <PanelHeader>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 11 12 14 22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
          Selected stages
        </PanelHeader>
        <PanelBody>
          {status.map((s) => (
            <Chip key={`${s.value}-${s.date}`}>
              <ChipLabel>{s.label}</ChipLabel>
              <ChipDot>·</ChipDot>
              <ChipDate>{moment(s.date).format("DD/MM/YYYY")}</ChipDate>
            </Chip>
          ))}
        </PanelBody>
      </SelectedStatusPanel>
    );
  };

  const handleClose = () => {
    setLifeCycleModal({ state: false });
    setStatus([]);
  };

  return (
    <Fragment>
      <Modal
        open={lifecycleModal?.state}
        onClose={handleClose}
        aria-labelledby="bulk-case-status-update-title"
        aria-describedby="bulk-case-status-update-description"
      >
        <Overlay>
          <ModalCard>
            {/* ── Header ── */}
            <ModalHeader>
              <HeaderAccent />
              <HeaderContent>
                <IconBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </IconBadge>
                <ModalTitle id="bulk-case-status-update-title">
                  Bulk Case Status Update
                </ModalTitle>
              </HeaderContent>
              <CloseBtn onClick={handleClose} aria-label="Close modal">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            {/* ── Formik Body ── */}
            <ModalBody>
              <Formik
                initialValues={{
                  startCaseId: "",
                  endCaseId: "",
                  exceptCaseIds: "",
                  stagesDate: null,
                  remarks: "",
                  fileName: "",
                  resolutionKind: "arbitration",
                }}
                validationSchema={
                  mode === inputModes.MANUAL ? AssigninBulkCaseId : undefined
                }
                onSubmit={onFormSubmit}
                enableReinitialize
              >
                {({
                  values,
                  handleChange,
                  handleSubmit,
                  errors,
                  touched,
                  handleBlur,
                  setFieldValue,
                  isValid,
                }) => {
                  formValuesRef.current = values;
                  const hasNewStatus = status.length > 0;
                  const canOpenSelector =
                    mode === inputModes.MANUAL
                      ? values.startCaseId && values.endCaseId
                      : excelCaseIds.length > 0;

                  return (
                    <StyledForm className="mis-dashboard">
                      {/* ── Case Selection ── */}
                      <FormSection>
                        <SectionLabel>Case Selection</SectionLabel>

                        <ModeToggle>
                          <ModeBtn
                            type="button"
                            active={mode === inputModes.MANUAL}
                            onClick={() => {
                              setMode(inputModes.MANUAL);
                              setExcelCaseIds([]);
                            }}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Enter ID Range
                          </ModeBtn>
                          <ModeBtn
                            type="button"
                            active={mode === inputModes.EXCEL}
                            onClick={() => setMode(inputModes.EXCEL)}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                            Upload Excel
                          </ModeBtn>
                        </ModeToggle>

                        {/* Manual Inputs Grid */}
                        {mode === inputModes.MANUAL ? (
                          <InputGrid>
                            {inputs.map((input, key) => (
                              <FormField
                                key={key}
                                white
                                placeholderText={input.placeholder || "N/A"}
                                maxDate={new Date()}
                                helperText={input.helperText}
                                menuItems={input.menuItems}
                                {...{
                                  input,
                                  values,
                                  errors,
                                  touched,
                                  handleBlur,
                                  handleChange,
                                  setFieldValue,
                                }}
                              />
                            ))}
                          </InputGrid>
                        ) : (
                          <>
                            <ExcelZone>
                              <label
                                htmlFor="excel-upload"
                                style={{ cursor: "pointer", display: "block" }}
                              >
                                <HiddenInput
                                  id="excel-upload"
                                  type="file"
                                  accept=".xlsx, .xls"
                                  onChange={(e) =>
                                    handleFileUpload(e, setFieldValue)
                                  }
                                />
                                <UploadInner loaded={excelCaseIds.length > 0}>
                                  {excelCaseIds.length > 0 ? (
                                    <>
                                      <UploadCheck>
                                        <svg
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      </UploadCheck>
                                      <UploadPrimary>
                                        {excelCaseIds.length} Case IDs Loaded
                                      </UploadPrimary>
                                      <UploadSub>
                                        Click to replace file
                                      </UploadSub>
                                    </>
                                  ) : (
                                    <>
                                      <UploadCloud>
                                        <svg
                                          width="28"
                                          height="28"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="16 16 12 12 8 16" />
                                          <line
                                            x1="12"
                                            y1="12"
                                            x2="12"
                                            y2="21"
                                          />
                                          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                        </svg>
                                      </UploadCloud>
                                      <UploadPrimary>
                                        Drop Excel file or click to browse
                                      </UploadPrimary>
                                      <UploadSub>
                                        Column must be named <code>caseId</code>
                                      </UploadSub>
                                    </>
                                  )}
                                </UploadInner>
                              </label>
                            </ExcelZone>
                            {[
                              {
                                type: "select",
                                name: "resolutionKind",
                                label: "Case Type",
                                required: true,
                                menuItems: [
                                  {
                                    label: "Arbitration",
                                    value: "arbitration",
                                  },
                                  { label: "Mediation", value: "mediation" },
                                  {
                                    label: "Negotiation",
                                    value: "negotiation",
                                  },
                                ],
                              },
                            ].map((input, key) => (
                              <FormField
                                key={key}
                                menuItems={input.menuItems}
                                white
                                {...{
                                  input,
                                  values,
                                  errors,
                                  touched,
                                  handleBlur,
                                  handleChange,
                                  setFieldValue,
                                }}
                              />
                            ))}
                          </>
                        )}
                      </FormSection>

                      <Divider />

                      {/* ── Remarks ── */}
                      <FormSection>
                        {[
                          {
                            type: "textarea",
                            name: "remarks",
                            label: "Case stages remarks",
                            required: false,
                            helperText:
                              "Optional: remarks are stored with the update for reference",
                          },
                        ].map((input, key) => (
                          <FormField
                            key={key}
                            white
                            {...{
                              input,
                              values,
                              errors,
                              touched,
                              handleBlur,
                              handleChange,
                              setFieldValue,
                            }}
                          />
                        ))}
                      </FormSection>

                      {existingStatuses.length > 0 && (
                        <FormSection>
                          <SectionLabel>
                            Current status in selected range
                          </SectionLabel>
                          <ExistingStatusBox role="region" aria-live="polite">
                            <ExistingStatusHeader>
                              <span>
                                {existingStatuses.length} found in this range
                              </span>
                              {loadingStatus && (
                                <CircularProgress
                                  size={14}
                                  style={{ marginLeft: 8, color: "#92400e" }}
                                />
                              )}
                            </ExistingStatusHeader>
                            <TagCloud>
                              {existingStatuses.map((s, i) => (
                                <StatusTag key={i} disabled>
                                  {s.label} <span className="dot">·</span>{" "}
                                  {moment(s.date).format("DD/MM/YYYY")}
                                </StatusTag>
                              ))}
                            </TagCloud>
                          </ExistingStatusBox>
                        </FormSection>
                      )}

                      <Divider />

                      <FormSection>
                        <SectionLabel>Stages to Apply</SectionLabel>
                        <StageCard
                          aria-label="Add or view selected stages"
                          filled={status?.length > 0}
                          onClick={() => setLifeCycle(true)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              setLifeCycle(true);
                          }}
                        >
                          <AddCircle filled={status?.length > 0}>
                            <AddIcon style={{ fontSize: 18 }} />
                          </AddCircle>
                          <StageInfo>
                            {status?.length ? (
                              <>
                                <StageName>
                                  {status.length} stage
                                  {status.length > 1 ? "s" : ""} selected
                                </StageName>
                                <StageHint>
                                  Click to modify selections
                                </StageHint>
                              </>
                            ) : (
                              <StagePlaceholder>
                                Click to select stages to apply
                              </StagePlaceholder>
                            )}
                          </StageInfo>
                          <ChevronIcon>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          </ChevronIcon>
                        </StageCard>

                        <SelectedStatusSummary />
                      </FormSection>

                      {/* ── Actions ── */}
                      <FooterActions>
                        <PrimaryOutlinedCloseCTAButton
                          type="button"
                          onClick={handleClose}
                          aria-label="Cancel and close"
                          style={{ flex: 1 }}
                        >
                          Cancel
                        </PrimaryOutlinedCloseCTAButton>
                        <PrimaryCTAButton
                          type="button"
                          disabled={
                            !hasNewStatus ||
                            !canOpenSelector ||
                            (mode === inputModes.MANUAL && !isValid)
                          }
                          onClick={() => setConfirmDialog(true)}
                          aria-label="Confirm bulk update"
                          style={{ flex: 1 }}
                        >
                          Update Cases
                        </PrimaryCTAButton>
                      </FooterActions>

                      {/* ── Confirm Dialog ── */}
                      <AlertDialog
                        isOpen={confirmDialog}
                        onDialogClose={() => setConfirmDialog(false)}
                        clickSecondarybtn={() => setConfirmDialog(false)}
                        primaryBtnText="Yes, update now"
                        secondaryBtnText="Cancel"
                        heading="Confirm bulk update stages"
                        desc={
                          <ConfirmContent>
                            <WarningBox role="note">
                              {mode === inputModes.MANUAL ? (
                                <>
                                  Updating cases from{" "}
                                  <strong>{values.startCaseId}</strong> to{" "}
                                  <strong>{values.endCaseId}</strong>
                                  {values.exceptCaseIds && (
                                    <> (except: {values.exceptCaseIds})</>
                                  )}
                                </>
                              ) : (
                                <>
                                  Updating{" "}
                                  <strong>{excelCaseIds.length}</strong> cases
                                  from Excel file
                                </>
                              )}
                            </WarningBox>
                            <p>Are you sure you want to update these stages?</p>
                            <DetailSection>
                              {status.map((s) => (
                                <div key={`${s.value}-${s.date}`}>
                                  • {s.label} on{" "}
                                  {moment(s.date).format("DD/MM/YYYY")}
                                </div>
                              ))}
                              {values.remarks && (
                                <div>
                                  <strong>Remarks:</strong> {values.remarks}
                                </div>
                              )}
                              <div>
                                <strong>Resolution Kind:</strong>{" "}
                                {
                                  [
                                    {
                                      label: "Arbitration",
                                      value: "arbitration",
                                    },
                                    { label: "Mediation", value: "mediation" },
                                    {
                                      label: "Negotiation",
                                      value: "negotiation",
                                    },
                                  ].find(
                                    (opt) =>
                                      opt.value === values.resolutionKind,
                                  )?.label
                                }
                              </div>
                              {values.stagesDate && (
                                <div>
                                  <strong>Stages date:</strong>{" "}
                                  {moment(values.stagesDate).format(
                                    "DD/MM/YYYY",
                                  )}
                                </div>
                              )}
                            </DetailSection>
                            <Note>
                              The stage with the latest date becomes the current
                              status. Earlier dates will update the timeline
                              only.
                            </Note>
                          </ConfirmContent>
                        }
                        clickPrimaryBtn={() => {
                          setConfirmDialog(false);
                          handleSubmit();
                        }}
                      />
                    </StyledForm>
                  );
                }}
              </Formik>
            </ModalBody>
          </ModalCard>
        </Overlay>
      </Modal>

      <ResponseModal
        open={showResponseModal?.state}
        onClose={() => setShowResponseModal({ state: false })}
        responseData={showResponseModal?.metaData}
      />

      <CaesLifeCycleStatus
        lifeCycle={lifeCycle}
        setLifeCycle={setLifeCycle}
        status={status}
        setStatus={setStatus}
        existingStatuses={existingStatuses}
      />
    </Fragment>
  );
};

export default Index;

const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 720px;
  background: #fff;
  border-radius: 20px;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.16),
    0 4px 16px rgba(0, 0, 0, 0.07);
  outline: none;
  animation: ${fadeIn} 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  display: flex;
  flex-direction: column;
  max-height: 95vh;

  @media (max-width: 480px) {
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    border-radius: 20px 20px 0 0;
    max-height: 92vh;
    animation: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f2f5;
  position: relative;
  flex-shrink: 0;
`;

const HeaderAccent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 20px 20px 0 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const IconBadge = styled.div`
  width: 36px;
  height: 36px;
  background: ${COLORS.BTN_GREEN}18;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.BTN_GREEN};
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  letter-spacing: -0.3px;
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  background: #f4f6f8;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.18s;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

/* Body */
const ModalBody = styled.div`
  overflow-y: auto;
  padding: 20px 24px 24px;
  flex: 1;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 99px;
  }
  @media (max-width: 480px) {
    padding: 16px 16px 24px;
  }
`;

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const FormSection = styled.section`
  margin-bottom: 18px;
`;

/* Labels */
const SectionLabel = styled.h4`
  margin: 0 0 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #9ca3af;
`;

/* Mode Toggle */
const ModeToggle = styled.div`
  display: flex;
  background: #f4f6f8;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
  margin-bottom: 16px;
`;

const ModeBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border: none;
  border-radius: 9px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  ${(p) =>
    p.active
      ? css`
          background: #fff;
          color: ${COLORS.BTN_GREEN};
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          font-weight: 700;
        `
      : css`
          background: transparent;
          color: #6b7280;
          &:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `}
  @media (max-width: 360px) {
    font-size: 12px;
    padding: 9px 6px;
  }
`;

/* Input Grid */
const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ExcelZone = styled.div`
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.2s;
  &:hover {
    border-color: ${COLORS.BTN_GREEN};
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadInner = styled.div`
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  background: ${(p) => (p.loaded ? `${COLORS.BTN_GREEN}08` : "#fafafa")};
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: ${COLORS.BTN_GREEN}08;
  }
`;

const UploadCloud = styled.div`
  color: #9ca3af;
  margin-bottom: 2px;
`;

const UploadCheck = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${COLORS.BTN_GREEN}20;
  color: ${COLORS.BTN_GREEN};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
`;

const UploadPrimary = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const UploadSub = styled.div`
  font-size: 12px;
  color: #9ca3af;
  code {
    background: #f3f4f6;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 11px;
    color: ${COLORS.BTN_GREEN};
  }
`;

/* Divider */
const Divider = styled.hr`
  border: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, #e5e7eb, transparent);
  margin: 4px 0 18px;
`;

const ExistingStatusBox = styled.div`
  background: #fffbeb;
  border: 1.5px solid #f59e0b;
  border-radius: 12px;
  padding: 14px;
`;

const ExistingStatusHeader = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #78350f;
  margin-bottom: 10px;
  font-size: 12px;
  display: flex;
  align-items: center;
`;

const TagCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatusTag = styled.span`
  padding: 5px 11px;
  border-radius: 99px;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: ${({ disabled }) => (disabled ? "#e5e7eb" : "#dbeafe")};
  color: ${({ disabled }) => (disabled ? "#374151" : "#1e40af")};
  display: inline-flex;
  align-items: center;
  gap: 5px;
  .dot {
    opacity: 0.6;
  }
`;

/* Stage Card */
const StageCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid ${(p) => (p.filled ? `${COLORS.BTN_GREEN}40` : "#e5e7eb")};
  background: ${(p) => (p.filled ? `${COLORS.BTN_GREEN}06` : "#fafafa")};
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: ${COLORS.BTN_GREEN};
    background: ${COLORS.BTN_GREEN}08;
  }
  &:focus {
    outline: 2px solid ${COLORS.BTN_GREEN};
    outline-offset: 2px;
  }
`;

const AddCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(p) =>
    p.filled ? `${COLORS.BTN_GREEN}20` : `${COLORS.BTN_GREEN}15`};
  color: ${COLORS.BTN_GREEN};
  transition: all 0.2s;
`;

const StageInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StageName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a2035;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const StageHint = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 1px;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const StagePlaceholder = styled.div`
  font-size: 13px;
  color: #9ca3af;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const ChevronIcon = styled.div`
  color: #9ca3af;
  flex-shrink: 0;
`;

/* Selected Status Panel */
const SelectedStatusPanel = styled.div`
  border: 1.5px dashed #cbd5e1;
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  margin-top: 12px;
`;

const PanelHeader = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #374151;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PanelBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 99px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e3a8a;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const ChipLabel = styled.span`
  font-weight: 600;
`;
const ChipDot = styled.span`
  opacity: 0.5;
`;
const ChipDate = styled.span`
  opacity: 0.8;
`;

/* Confirm Dialog Content */
const ConfirmContent = styled.div`
  line-height: 1.7;
  font-size: 14px;
`;

const WarningBox = styled.div`
  background: #fef3c7;
  border: 1.5px solid #f59e0b;
  border-radius: 10px;
  padding: 14px;
  margin: 8px 0 14px;
  color: #92400e;
  font-weight: 500;
  font-size: 14px;
`;

const DetailSection = styled.div`
  margin: 10px 0 0;
  strong {
    color: #1a2035;
    font-weight: 600;
  }
  div {
    margin: 6px 0;
    font-size: 13px;
    color: #374151;
  }
`;

const Note = styled.div`
  margin-top: 12px;
  font-size: 12.5px;
  color: #334155;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
`;

/* Footer */
const FooterActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid #f0f2f5;
  @media (max-width: 400px) {
    flex-direction: column-reverse;
  }
`;
